import { useState } from 'react';
import { Button, Dialog } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Field } from '../card';
import { DateField } from '../date-field';
import { todayWire } from '../delivery/project-screen';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 5 Nov 2026 · "add them to the project" — from the resource planner.       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner's pick of three: a line item on a project the person is not on stays disabled
 * in *Sortétel hozzáadása*, but a reader who may manage that project's people can open THIS — the
 * person, the project, the first day (today, changeable) and no end — and on *Hozzáadás* the
 * membership is written AND the line item lands in the allocation the picker was opened from.
 *
 * ⚠️ **A dialog rather than one click**, because the membership is more than a planning detail: it
 * is the right to log time on the project (P-1), so the reader sees exactly what they grant.
 */
export function JoinProjectDialog({
  personName,
  projectName,
  lineItemName,
  onCancel,
  onJoin,
}: {
  readonly personName: string;
  readonly projectName: string;
  readonly lineItemName: string;
  readonly onCancel: () => void;
  /** Resolves `true` once the membership is written — the dialog closes; `false` keeps it open. */
  readonly onJoin: (validFrom: string) => Promise<boolean>;
}) {
  const t = useT();
  const [from, setFrom] = useState(todayWire());
  const [busy, setBusy] = useState(false);

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onCancel();
      }}
      title={t('resource.join.title')}
      closeLabel={t('common.close')}
      width={420}
    >
      <div className="flex flex-col gap-3">
        <p className="text-[13px]">
          {t('resource.join.body', {
            person: personName,
            project: projectName,
            lineItem: lineItemName,
          })}
        </p>
        <Field label={t('project.people.column.from')}>
          <DateField
            label={t('project.people.column.from')}
            value={from}
            clearable={false}
            onChange={setFrom}
          />
        </Field>
        <Field label={t('project.people.column.until')}>
          <p className="text-muted-foreground text-[13px]">{t('resource.join.open')}</p>
        </Field>
        <div className="flex items-center gap-2">
          <div className="flex-1" />
          <Button variant="outline" disabled={busy} onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={busy || from === ''}
            onClick={() => {
              setBusy(true);
              void onJoin(from).then((joined) => {
                setBusy(false);
                if (joined) onCancel();
              });
            }}
          >
            {t('resource.join.action')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
